import { registerLocaleData } from '@angular/common';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import localeNl from '@angular/common/locales/nl';
import {
  LOCALE_ID,
  enableProdMode,
  inject,
  isDevMode,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import {
  PreloadAllModules,
  RouteReuseStrategy,
  provideRouter,
  withPreloading,
} from '@angular/router';
import {
  MsalBroadcastService,
  MsalGuard,
  MsalModule,
  MsalService,
} from '@azure/msal-angular';
import {
  BrowserCacheLocation,
  InteractionType,
  PublicClientApplication,
} from '@azure/msal-browser';
import {
  IonicRouteStrategy,
  provideIonicAngular,
} from '@ionic/angular/standalone';
import { EnvironmentInjector } from '@next/core-lib/environment';
import { provideEffects } from '@ngrx/effects';
import { provideRouterStore, routerReducer } from '@ngrx/router-store';
import { Store, provideState, provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { provideHighcharts } from 'highcharts-angular';

import { AppComponent } from './app/app.component';
import { dataInitializer } from './app/app.initializer';
import { routes } from './app/app.routes';
import { LocationInterceptor } from './app/router/location.interceptor';
import { TokenInterceptor } from './app/router/token.interceptor';
import { loadedConfig, setupRuntimeConfig } from './app/runtime-config';
import { AuthenticationService } from './app/services/authentication.service';
import { SettingsService } from './app/services/settings.service';
import { ApplicationInsightsEffects } from './app/store/application-insights/effects';
import { LabResultsEffects } from './app/store/lab-results/effects';
import { labResultsFeature } from './app/store/lab-results/feature';
import { MonitorEffects } from './app/store/monitor/effects';
import { monitorFeature } from './app/store/monitor/feature';
import { NotificationEffects } from './app/store/notifications/effects';
import { notificationFeature } from './app/store/notifications/feature';
import { SettingsEffects } from './app/store/settings/effects';
import { settingsFeature } from './app/store/settings/feature';
import { userFeature } from './app/store/user/feature';
import {
  environment,
  RoughagePlusEnvironment,
} from './environments/environment';

registerLocaleData(localeNl);

if (environment.PRODUCTION) {
  enableProdMode();
}

const bootstrapPromise = setupRuntimeConfig.then(() => {
  const runtimeEnv = loadedConfig();
  return bootstrapApplication(AppComponent, {
    providers: [
      { provide: EnvironmentInjector, useFactory: () => loadedConfig() },
      provideZoneChangeDetection(),
      provideRouter(routes, withPreloading(PreloadAllModules)),
      provideIonicAngular(),
      provideHighcharts(),
      { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
      provideHttpClient(withInterceptorsFromDi()),
      // MSAL providers
      ...MsalModule.forRoot(
        new PublicClientApplication({
          auth: {
            clientId: runtimeEnv.CLIENT_ID,
            authority: `${runtimeEnv.AUTHORITY}/${runtimeEnv.SIGNUP_SIGNIN_POLICY}`,
            redirectUri: runtimeEnv.REDIRECT_URL,
            knownAuthorities: [runtimeEnv.AUTHORITY],
            postLogoutRedirectUri: runtimeEnv.REDIRECT_URL,
          },
          cache: {
            cacheLocation: BrowserCacheLocation.LocalStorage,
          },
          system: {
            loggerOptions: {
              loggerCallback: runtimeEnv.MSAL_LOGGING
                ? (lvl, msg) => console.log(lvl, msg)
                : undefined,
            },
          },
        }),
        {
          interactionType: InteractionType.Redirect, // MSAL Guard Configuration
          loginFailedRoute: '/auth',
        },
        {
          interactionType: InteractionType.Redirect, // MSAL Interceptor Configuration
          protectedResourceMap: new Map(), // Empty on purpose, we have our own interceptor (TokenInterceptor)
        },
      ).providers!,
      MsalService,
      MsalGuard,
      MsalBroadcastService,
      // NgRx Store
      provideStore({ router: routerReducer }),
      provideState(monitorFeature),
      provideState(settingsFeature),
      provideState(labResultsFeature),
      provideState(notificationFeature),
      provideState(userFeature),
      provideEffects([
        SettingsEffects,
        MonitorEffects,
        LabResultsEffects,
        NotificationEffects,
        ApplicationInsightsEffects,
      ]),
      provideRouterStore(),
      provideStoreDevtools({
        maxAge: 25,
        logOnly: !isDevMode(),
        autoPause: true,
        trace: false,
        traceLimit: 75,
        connectInZone: false,
      }),
      // HTTP interceptors
      {
        provide: HTTP_INTERCEPTORS,
        useClass: TokenInterceptor,
        multi: true,
      },
      {
        provide: HTTP_INTERCEPTORS,
        useClass: LocationInterceptor,
        multi: true,
      },
      // App-level providers
      { provide: RoughagePlusEnvironment, useValue: runtimeEnv },
      { provide: LOCALE_ID, useValue: 'nl' },
      provideAppInitializer(() => {
        const initializerFn = dataInitializer(
          inject(AuthenticationService),
          inject(MsalBroadcastService),
          inject(Store),
          inject(SettingsService),
          inject(MsalService),
        );
        return initializerFn();
      }),
    ],
  });
});
bootstrapPromise.catch((err) => console.error(err));
